/**
 * Location Testing Utility
 * 
 * This utility can be called from the browser console to test location fetching.
 * 
 * Usage in browser console:
 *   window.testLocation()
 *   window.testLocationPermission()
 *   window.testLocationContext()
 */

import Logger from '../services/logger';

// Make functions available globally for console testing
declare global {
  interface Window {
    testLocation: () => Promise<void>;
    testLocationPermission: () => Promise<void>;
    testLocationContext: () => void;
  }
}

/**
 * Test basic geolocation API
 */
export const testLocation = async (): Promise<void> => {
  console.log('🧪 Testing Geolocation API...');
  
  if (typeof navigator === 'undefined') {
    console.error('❌ Navigator is not available');
    return;
  }
  
  if (!('geolocation' in navigator)) {
    console.error('❌ Geolocation is not supported by this browser');
    return;
  }
  
  console.log('✅ Geolocation API is available');
  
  // Check secure context
  const isSecure = window.isSecureContext || 
                  window.location.protocol === 'https:' || 
                  window.location.hostname === 'localhost' || 
                  window.location.hostname === '127.0.0.1';
  
  if (!isSecure) {
    console.error('❌ Not in secure context. Geolocation requires HTTPS or localhost.');
    console.log('Current protocol:', window.location.protocol);
    console.log('Current hostname:', window.location.hostname);
    return;
  }
  
  console.log('✅ Secure context check passed');
  console.log('📍 Requesting location with high accuracy...');
  
  return new Promise((resolve) => {
    const startTime = Date.now();
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const duration = Date.now() - startTime;
        const loc: [number, number] = [position.coords.longitude, position.coords.latitude];
        
        console.log('✅ Location obtained successfully!');
        console.log('📍 Coordinates:', loc);
        console.log('🎯 Accuracy:', position.coords.accuracy, 'meters');
        console.log('⏱️  Time taken:', duration, 'ms');
        console.log('📊 Full position data:', {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          altitude: position.coords.altitude,
          altitudeAccuracy: position.coords.altitudeAccuracy,
          heading: position.coords.heading,
          speed: position.coords.speed,
          timestamp: new Date(position.timestamp).toISOString()
        });
        
        Logger.info('[LocationTest] Location test successful', {
          location: loc,
          accuracy: position.coords.accuracy,
          duration
        });
        
        resolve();
      },
      (error) => {
        const duration = Date.now() - startTime;
        let errorMessage = 'Unknown error';
        
        switch (error.code) {
          case 1: // PERMISSION_DENIED
            errorMessage = 'Permission denied by user';
            console.error('❌ Permission denied. Please allow location access in your browser settings.');
            break;
          case 2: // POSITION_UNAVAILABLE
            errorMessage = 'Position unavailable';
            console.error('❌ Position unavailable. Check your device location settings.');
            break;
          case 3: // TIMEOUT
            errorMessage = 'Request timed out';
            console.error('❌ Request timed out. Ensure location services are enabled.');
            break;
          default:
            errorMessage = error.message || 'Unknown error';
            console.error('❌ Error:', errorMessage);
        }
        
        console.error('⏱️  Time taken before error:', duration, 'ms');
        console.error('📊 Full error data:', {
          code: error.code,
          message: error.message,
          PERMISSION_DENIED: 1,
          POSITION_UNAVAILABLE: 2,
          TIMEOUT: 3
        });
        
        Logger.error('[LocationTest] Location test failed', new Error(errorMessage), {
          errorCode: error.code,
          duration
        });
        
        resolve();
      },
      {
        enableHighAccuracy: true,
        timeout: 30000, // PRODUCTION FIX: Increased to 30s for high accuracy GPS
        maximumAge: 0
      }
    );
  });
};

/**
 * Test location permission status
 */
export const testLocationPermission = async (): Promise<void> => {
  console.log('🔐 Testing Location Permission Status...');
  
  if (typeof navigator === 'undefined' || !navigator.permissions) {
    console.warn('⚠️  Permissions API not available');
    return;
  }
  
  try {
    const result = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
    console.log('📋 Permission status:', result.state);
    
    result.onchange = () => {
      console.log('🔄 Permission status changed to:', result.state);
    };
    
    Logger.debug('[LocationTest] Permission status', { state: result.state });
  } catch (error) {
    console.error('❌ Error checking permission:', error);
    Logger.error('[LocationTest] Permission check failed', error as Error);
  }
};

/**
 * Test LocationContext (requires LocationContext to be available)
 */
export const testLocationContext = (): void => {
  console.log('🔍 Testing LocationContext...');
  
  // This will be called from browser console after LocationContext is loaded
  // The user should call: window.testLocationContext()
  
  console.log('ℹ️  To test LocationContext, check the React DevTools or use the retry button in the UI.');
  console.log('ℹ️  LocationContext should be available via useLocationContext() hook.');
  
  // Try to access LocationContext if available
  const locationContext = (window as any).__LOCATION_CONTEXT__;
  if (locationContext) {
    console.log('✅ LocationContext found:', locationContext);
  } else {
    console.log('ℹ️  LocationContext not exposed globally. Use React DevTools to inspect.');
  }
};

// Expose functions to window for console access
if (typeof window !== 'undefined') {
  window.testLocation = testLocation;
  window.testLocationPermission = testLocationPermission;
  window.testLocationContext = testLocationContext;
  
  console.log('🧪 Location test utilities loaded!');
  console.log('📝 Available commands:');
  console.log('   window.testLocation() - Test basic geolocation API');
  console.log('   window.testLocationPermission() - Check permission status');
  console.log('   window.testLocationContext() - Test LocationContext (if available)');
}
